import React, { useState } from 'react';
import { 
  Code, 
  Cloud, 
  Smartphone, 
  Database, 
  Globe, 
  ArrowRight, 
  Users, 
  Settings, 
  Layers, 
  CheckCircle2, 
  Zap, 
  Sparkles, 
  RefreshCw, 
  Search, 
  Building2, 
  CreditCard, 
  TrendingUp, 
  ShieldCheck,
  Server,
  Activity,
  Headphones
} from 'lucide-react';

export interface ServiceItem {
  id: string;
  number: number;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
  badge: string;
  category: 'web-app' | 'enterprise' | 'cloud-api' | 'maintenance';
  offerings: string[];
  technologies?: string[];
  benefits?: string[];
  model?: string;
}

export const COMPANY_SERVICES: ServiceItem[] = [
  {
    id: 'web-dev',
    number: 1,
    icon: <Globe className="h-7 w-7" />,
    title: 'Web Development & Portals',
    subtitle: 'High-Converting Web Experiences & Enterprise Portals',
    description: 'From lightning-fast static business websites to full-stack dynamic platforms, corporate portals, and integrated e-commerce solutions.',
    badge: 'Popular & Core',
    category: 'web-app',
    offerings: [
      'Static Business Websites',
      'Dynamic Web Applications',
      'E-Commerce Websites & Stores',
      'Corporate & Enterprise Websites',
      'Portfolio & Brand Showcase Websites',
      'High-Conversion Landing Pages',
      'Website Redesign & Speed Optimization',
      'SEO-Friendly Architectural Development',
      'Payment Gateway Integration (Razorpay, Stripe, PayPal)'
    ],
    technologies: ['PHP', 'Java', '.NET', 'WordPress', 'Laravel', 'React', 'HTML5', 'CSS3', 'JavaScript', 'TailwindCSS'],
    benefits: ['Sub-second page load times', 'SEO top-ranking architecture', '100% mobile-responsive layout', 'Secure checkout conversion']
  },
  {
    id: 'crm-erp',
    number: 2,
    icon: <Building2 className="h-7 w-7" />,
    title: 'CRM / ERP Development',
    subtitle: 'High-Value Enterprise Management & Automation',
    description: 'Custom-built enterprise resource planning and CRM software engineered to unify business departments, eliminate manual paperwork, and drive profitability.',
    badge: 'High-Value Projects',
    category: 'enterprise',
    offerings: [
      'Custom CRM Solutions Tailored to Workflows',
      'Sales CRM with Lead Pipeline Tracking',
      'HRMS (Payroll, Biometric Attendance, Leaves)',
      'Billing, GST & Automated Accounting Software',
      'Real-Time Inventory Management Systems',
      'School & College Management Systems',
      'Hospital & Clinic ERP Platforms',
      'Real Estate Property & Lead CRM',
      'Manufacturing & Supply Chain ERP'
    ],
    benefits: [
      'End-to-end operational automation',
      'Massive cost savings & paperwork reduction',
      'Up to 45% increase in team productivity',
      'Elimination of human calculation errors'
    ],
    technologies: ['React', 'Laravel', 'PHP MVC', '.NET Core', 'Java Spring', 'MySQL', 'PostgreSQL']
  },
  {
    id: 'mobile-app',
    number: 3,
    icon: <Smartphone className="h-7 w-7" />,
    title: 'Mobile App Development',
    subtitle: 'Native iOS & Android and Modern Cross-Platform Apps',
    description: 'Fast, fluid, and intuitive mobile experiences designed to capture customers on iOS and Android with 60fps animations and offline capabilities.',
    badge: 'Fast Growing Demand',
    category: 'web-app',
    offerings: [
      'Native Android App Development (Kotlin / Java)',
      'Native iOS App Development (Swift)',
      'Cross-Platform Apps (Flutter & React Native)',
      'Corporate & B2B Business Apps',
      'E-Commerce & Retail Marketplace Apps',
      'Booking, Scheduling & Appointment Apps',
      'Delivery & Live GPS Tracking Applications',
      'Educational & Interactive E-Learning Apps'
    ],
    technologies: ['Flutter', 'React Native', 'Java', 'Kotlin', 'Swift', 'Firebase', 'RESTful APIs'],
    benefits: [
      'Single codebase delivery for Android & iOS',
      'Push notification engagement funnels',
      'Guaranteed Google Play & Apple App Store approval',
      'Biometric fingerprint & FaceID logins'
    ]
  },
  {
    id: 'api-dev',
    number: 4,
    icon: <Zap className="h-7 w-7" />,
    title: 'API Development & Integration',
    subtitle: 'Secure, High-Throughput Digital Connectivity',
    description: 'Robust API endpoints, microservices, and third-party integrations that connect your disparate software systems into one coordinated machine.',
    badge: 'Modern Business Vital',
    category: 'cloud-api',
    offerings: [
      'RESTful & GraphQL API Development',
      'Payment Gateway Integrations (Stripe, Razorpay, Cashfree)',
      'Automated SMS, Email & WhatsApp Business APIs',
      'ERP & CRM Bi-Directional System Sync',
      'High-Speed Mobile App Backend APIs',
      'Government, GST & Banking API Integration',
      'Third-Party Webhooks & Event Streaming'
    ],
    technologies: ['Node.js', 'PHP', 'Python', 'OAuth 2.0', 'JWT', 'Swagger / OpenAPI', 'Postman'],
    benefits: [
      '99.99% API uptime reliability',
      'Bank-grade TLS encryption & token auth',
      'Automated cross-platform data synchronization',
      'Zero manual data re-entry across tools'
    ]
  },
  {
    id: 'cloud-solutions',
    number: 5,
    icon: <Cloud className="h-7 w-7" />,
    title: 'Cloud Solutions & DevOps',
    subtitle: 'Resilient Infrastructure, Migration & Cost Optimization',
    description: 'Production-ready cloud architectures, containerized Docker deployments, and zero-downtime server migrations with round-the-clock uptime monitoring.',
    badge: '99.9% Uptime Guarantee',
    category: 'cloud-api',
    offerings: [
      'Cloud Hosting & Enterprise Server Setup',
      'Continuous Server Deployment & Maintenance',
      'Cloud Migration (On-Premise to Cloud)',
      'AWS, Microsoft Azure & Google Cloud (GCP) Management',
      'Database Clustering, Tuning & Optimization',
      'Automated Backup & Disaster Recovery Solutions',
      'Cloud Security Hardening & Hosting Cost Optimization'
    ],
    technologies: ['AWS', 'Microsoft Azure', 'Google Cloud (GCP)', 'Docker', 'Kubernetes', 'Linux (Ubuntu/CentOS)', 'Nginx'],
    benefits: [
      'Automated auto-scaling during high traffic surges',
      'Up to 35% reduction in cloud server bills',
      'Disaster recovery with hourly snapshot backups',
      'Strict SSL, firewall, and DDoS mitigation'
    ]
  },
  {
    id: 'software-maintenance',
    number: 6,
    icon: <Headphones className="h-7 w-7" />,
    title: 'Software Maintenance & Support',
    subtitle: 'SLA-Backed AMC, Proactive Monitoring & Rapid Bug Fixing',
    description: 'Keep your web platforms, mobile apps, and servers running at peak health with dedicated Annual Maintenance Contracts (AMC) and 24/7 technical assistance.',
    badge: 'Recurring AMC Model',
    category: 'maintenance',
    offerings: [
      'Rapid Bug Fixing & Urgent Troubleshooting',
      'Continuous Feature Enhancements & UI Updates',
      'Codebase Performance Optimization & Query Tuning',
      'Security Patching, Vulnerability Auditing & SSL Renewal',
      '24/7 Server Monitoring & Proactive Uptime Support',
      'Comprehensive Annual Maintenance Contract (AMC)',
      'Dedicated Tier-1 & Tier-2 Developer Support'
    ],
    technologies: ['Git Version Control', 'Sentry Error Tracking', 'UptimeRobot', 'Jira Issue Tracking', 'PHP / JS Patches'],
    benefits: [
      'Guaranteed SLA response times under 2 hours',
      'Zero unplanned server downtime',
      'Continuous defense against security exploits',
      'Dedicated support engineer assigned to your account'
    ]
  },
  {
    id: 'custom-dev',
    number: 7,
    icon: <Code className="h-7 w-7" />,
    title: 'Custom PHP / React / App Development',
    subtitle: 'Bespoke Application Engineering Built to Exact Scope',
    description: 'Tailor-made software engineered precisely around your proprietary business workflows without the restrictive bloat or license fees of generic tools.',
    badge: 'Bespoke Engineering',
    category: 'web-app',
    offerings: [
      'Custom Full-Stack Web Applications',
      'Executive Admin Panels & Interactive Dashboards',
      'Custom Analytics & Financial Reporting Tools',
      'Internal Company Workstation & Employee Portals',
      'Startup MVP Development for Rapid Investor Demos'
    ],
    technologies: ['Custom PHP MVC', 'Laravel Framework', 'React.js', 'TailwindCSS', 'Node.js', 'MySQL / MariaDB'],
    benefits: [
      '100% intellectual property (IP) and source code ownership',
      'Clean modular architecture easy to expand in the future',
      'Rapid turnaround for startup MVPs in 2 to 4 weeks',
      'Zero monthly per-user licensing fees'
    ]
  },
  {
    id: 'saas-solutions',
    number: 8,
    icon: <TrendingUp className="h-7 w-7" />,
    title: 'SaaS-Based Solutions',
    subtitle: 'Scalable Subscription Software for Recurring Growth',
    description: 'Turn your software vision into a recurring subscription business with multi-tenant cloud architectures, automated recurring billing, and user onboarding.',
    badge: 'Subscription Model',
    category: 'enterprise',
    offerings: [
      'HRMS SaaS (Monthly / Yearly Subscription Tiers)',
      'CRM SaaS for Small & Medium Enterprises',
      'Billing, Invoicing & GST Subscription Platforms',
      'School Attendance, Fee & Parent Portal SaaS',
      'Multi-Branch Inventory & Warehousing SaaS',
      'Subscription-Based Mobile Apps with In-App Billing'
    ],
    technologies: ['Multi-Tenant Database Design', 'Stripe / Razorpay Subscriptions', 'JWT Authentication', 'Docker', 'PostgreSQL / MySQL'],
    benefits: [
      'Predictable recurring monthly/annual revenue streams',
      'Automated tenant provisioning and payment renewal',
      'Self-service registration & onboarding flow',
      'Enterprise-grade data isolation between tenant organizations'
    ]
  }
];

const Services: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<'all' | 'web-app' | 'enterprise' | 'cloud-api' | 'maintenance'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(null);

  const categories = [
    { id: 'all', name: 'All Services (8)' },
    { id: 'web-app', name: 'Web & Mobile Apps' },
    { id: 'enterprise', name: 'CRM, ERP & SaaS' },
    { id: 'cloud-api', name: 'Cloud & APIs' },
    { id: 'maintenance', name: 'AMC & Support' }
  ];

  const filteredServices = COMPANY_SERVICES.filter(service => {
    const matchesCat = activeCategory === 'all' || service.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.offerings.some(o => o.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (service.technologies && service.technologies.some(t => t.toLowerCase().includes(searchQuery.toLowerCase())));
    return matchesCat && matchesSearch;
  });

  const handleRequestQuote = (serviceTitle: string) => {
    window.location.hash = '#contact';
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-white via-green-50/20 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <span className="bg-brand-primary/10 text-brand-dark px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
            Full-Spectrum Technology Capabilities
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-brand-secondary tracking-tight">
            Our Core <span className="text-brand-primary">Services & Solutions</span>
          </h2>
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            From high-conversion web development and enterprise CRM/ERP systems to native mobile apps, 
            cloud architecture, and scalable SaaS platforms. Built by engineers who deliver results.
          </p>
        </div>

        {/* Controls: Search & Category Filter Tabs */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search services or tech stack..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all outline-none"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600 font-bold"
              >
                Clear
              </button>
            )}
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id as any)}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
                  activeCategory === cat.id 
                    ? 'bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] text-white shadow-md shadow-[#14c9cb]/30 scale-105' 
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-[#14c9cb] hover:text-[#0d9488] hover:bg-teal-50/50'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Services Grid (8 Comprehensive Pillars) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredServices.map((service) => (
            <div 
              key={service.id}
              className="bg-white border border-gray-200/90 rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1.5 group hover:border-[#14c9cb] flex flex-col justify-between relative overflow-hidden"
            >
              {/* Top Corner Number Accent */}
              <div className="absolute top-4 right-4 text-3xl font-black text-gray-100 group-hover:text-teal-100 transition-colors pointer-events-none">
                0{service.number}
              </div>

              <div>
                {/* Header: Icon + Badge */}
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="text-[#0d9488] bg-[#f0fdf9] p-3 rounded-xl group-hover:scale-110 group-hover:bg-gradient-to-r group-hover:from-[#10b981] group-hover:to-[#14c9cb] group-hover:text-white transition-all duration-300 shadow-sm">
                    {service.icon}
                  </div>
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/60 uppercase tracking-wide">
                    {service.badge}
                  </span>
                </div>

                {/* Title & Subtitle */}
                <h3 className="text-xl font-bold text-brand-secondary group-hover:text-brand-primary transition-colors">
                  {service.title}
                </h3>
                <p className="text-xs font-semibold text-brand-accent mt-0.5 mb-2">
                  {service.subtitle}
                </p>
                <p className="text-gray-600 text-xs leading-relaxed mb-4">
                  {service.description}
                </p>

                {/* Sub-Services Checklist */}
                <div className="border-t border-gray-100 pt-3 pb-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2">
                    Key Offerings:
                  </div>
                  <ul className="space-y-1.5">
                    {service.offerings.slice(0, 5).map((offering, idx) => (
                      <li key={idx} className="text-xs text-gray-700 flex items-start gap-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-brand-primary flex-shrink-0 mt-0.5" />
                        <span>{offering}</span>
                      </li>
                    ))}
                    {service.offerings.length > 5 && (
                      <li className="text-[11px] font-semibold text-brand-primary pl-5">
                        + {service.offerings.length - 5} more capabilities
                      </li>
                    )}
                  </ul>
                </div>

                {/* Technologies Stack Tags */}
                {service.technologies && service.technologies.length > 0 && (
                  <div className="pt-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                      Technologies & Stack:
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {service.technologies.map((tech, idx) => (
                        <span 
                          key={idx}
                          className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-[10px] font-medium border border-gray-200"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Client Benefits Snippet */}
                {service.benefits && service.benefits.length > 0 && (
                  <div className="mt-3 p-2.5 rounded-lg bg-emerald-50/70 border border-emerald-100 text-[11px] text-emerald-900">
                    <span className="font-bold text-emerald-800">Client Value: </span>
                    {service.benefits[0]}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between gap-2">
                <button 
                  onClick={() => setSelectedService(service)}
                  className="text-xs font-semibold text-gray-600 hover:text-brand-primary transition-colors underline-offset-2 hover:underline"
                >
                  View Details
                </button>
                <button 
                  onClick={() => handleRequestQuote(service.title)}
                  className="flex items-center text-xs font-bold bg-brand-primary text-white px-3.5 py-1.5 rounded-lg hover:bg-brand-dark transition-all duration-200 group-hover:translate-x-0.5 shadow-sm"
                >
                  Request Quote
                  <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <div className="bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] rounded-3xl p-8 sm:p-12 text-white shadow-xl shadow-[#14c9cb]/20 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
          {/* Subtle Ambient Glow */}
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>

          <div className="space-y-2 max-w-2xl text-center md:text-left relative z-10">
            <span className="bg-white/20 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider inline-block">
              Custom Engineering Blueprint
            </span>
            <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Have a custom project or enterprise scope in mind?
            </h3>
            <p className="text-white/90 text-sm sm:text-base leading-relaxed">
              We provide fast turnaround architectural blueprints, transparent sprint milestones, 
              and dedicated engineering support under NDAs.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto relative z-10">
            <button 
              onClick={() => { window.location.hash = '#contact'; }}
              className="bg-white text-[#0f766e] px-6 py-3 rounded-xl font-bold hover:bg-[#f0fdf9] transition-all text-center shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            >
              Get Free Consultation
            </button>
            <a 
              href="tel:+919074140432"
              className="bg-white/20 backdrop-blur-md border border-white/40 text-white px-6 py-3 rounded-xl font-bold hover:bg-white/30 transition-all text-center transform hover:scale-[1.02]"
            >
              Call: +91 90741 40432
            </a>
          </div>
        </div>
      </div>

      {/* Detail Modal for Selected Service */}
      {selectedService && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 sm:p-8 space-y-6 max-h-[90vh] overflow-y-auto relative animate-scale-in">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="text-brand-primary bg-brand-light p-3 rounded-xl">
                  {selectedService.icon}
                </div>
                <div>
                  <span className="text-xs font-bold text-brand-primary uppercase tracking-wide">
                    {selectedService.badge}
                  </span>
                  <h3 className="text-xl sm:text-2xl font-bold text-brand-secondary">
                    {selectedService.title}
                  </h3>
                  <p className="text-xs text-gray-500 font-medium">
                    {selectedService.subtitle}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedService(null)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed">
              {selectedService.description}
            </p>

            {/* All Offerings */}
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Full Scope of Capabilities:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {selectedService.offerings.map((offering, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-gray-50 text-xs text-gray-800">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary flex-shrink-0 mt-0.5" />
                    <span>{offering}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Technologies */}
            {selectedService.technologies && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Supported Technologies & Frameworks:
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedService.technologies.map((tech, idx) => (
                    <span 
                      key={idx}
                      className="px-2.5 py-1 rounded-lg bg-green-50 text-green-800 text-xs font-semibold border border-green-200"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Client Benefits */}
            {selectedService.benefits && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Direct Benefits to Client:
                </h4>
                <div className="space-y-1.5">
                  {selectedService.benefits.map((b, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-gray-700">
                      <div className="w-1.5 h-1.5 bg-brand-primary rounded-full"></div>
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
              <button 
                onClick={() => setSelectedService(null)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
              <button 
                onClick={() => {
                  setSelectedService(null);
                  handleRequestQuote(selectedService.title);
                }}
                className="px-5 py-2 rounded-xl text-xs font-bold bg-brand-primary text-white hover:bg-brand-dark transition-all shadow-md"
              >
                Request Architecture Blueprint
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Services;